import React, { useState, useEffect, useCallback } from "react";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Search,
  ChevronRight,
  Database,
  Table,
  FileText,
  Loader,
  Filter,
  Eye,
  EyeOff,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import axiosClient from "../../../../api-client/AxiosClient";

const toast = {
  error: (msg) => console.error(msg),
  warning: (msg) => console.warn(msg),
  info: (msg) => console.info(msg),
};

// Custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("custom-scrollbar-styles");
  if (!existingStyle) {
    const style = document.createElement("style");
    style.id = "custom-scrollbar-styles";
    style.textContent = scrollbarStyles;
    document.head.appendChild(style);
  }
}

// Custom Node Components
const ObjectNode = ({ data }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section
      className="rounded-full border-4 border-orange-500 bg-gradient-to-br from-orange-100 to-orange-200 shadow-2xl flex items-center justify-center relative hover:shadow-3xl transition-all cursor-pointer"
      style={{ width: "160px", height: "160px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={`Object node: ${data?.label || "Unknown"}`}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      <div className="text-center px-4">
        <div className="text-lg font-bold text-gray-900 leading-tight break-words">
          {data.label}
        </div>
        <div className="text-xs text-gray-600 mt-2 font-semibold uppercase">
          Object
        </div>
      </div>

      {isHovered && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-4 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-2xl z-50 min-w-[200px]">
          <div className="text-xs font-bold text-orange-300">
            📦 Central Object
          </div>
        </div>
      )}
    </section>
  );
};

ObjectNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }),
};

const TargetNode = ({ data }) => {
  return (
    <section
      className="rounded-full border-3 bg-gradient-to-br from-cyan-100 to-cyan-200 border-cyan-500 shadow-lg hover:shadow-2xl transition-all flex items-center justify-center cursor-pointer"
      style={{ width: "130px", height: "130px" }}
      aria-label={`Target node: ${data?.label || "Unknown"}`}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      <div className="text-center px-3">
        <div className="text-sm font-bold text-gray-900 leading-tight break-words">
          {data.label}
        </div>
        <div className="text-xs text-gray-600 mt-1.5 font-semibold">Target</div>
      </div>
    </section>
  );
};

TargetNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }),
};

const SourceNode = ({ data }) => {
  return (
    <section
      className="rounded-full border-3 bg-gradient-to-br from-green-100 to-green-200 border-green-500 shadow-lg hover:shadow-2xl transition-all flex items-center justify-center cursor-pointer"
      style={{ width: "130px", height: "130px" }}
      aria-label={`Source node: ${data?.label || "Unknown"}`}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      <div className="text-center px-3">
        <div className="text-sm font-bold text-gray-900 leading-tight break-words">
          {data.label}
        </div>
        <div className="text-xs text-gray-600 mt-1.5 font-semibold">Source</div>
      </div>
    </section>
  );
};

SourceNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }),
};

const ViewNode = ({ data }) => {
  return (
    <section
      className="rounded-full border-3 bg-gradient-to-br from-purple-100 to-purple-200 border-purple-500 shadow-lg hover:shadow-2xl transition-all flex items-center justify-center cursor-pointer"
      style={{ width: "120px", height: "120px" }}
      aria-label={`View node: ${data?.label || "Unknown"}`}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      <div className="text-center px-3">
        <div className="text-sm font-bold text-gray-900 leading-tight break-words">
          {data.label}
        </div>
        <div className="text-xs text-gray-600 mt-1.5 font-semibold">View</div>
      </div>
    </section>
  );
};

ViewNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }),
};

const CommonColumnEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  markerEnd,
  style,
  data,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const edgePath = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  const labelX = (sourceX + targetX) / 2;
  const labelY = (sourceY + targetY) / 2;

  return (
    <g>
      {/* Invisible wider path for hover detection */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: "pointer" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />

      {/* Visible edge path */}
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        fill="none"
        pointerEvents="none"
      />

      {/* Edge label */}
      <text pointerEvents="none">
        <textPath
          href={`#${id}`}
          style={{ fontSize: 10, fill: "#6b21a8", fontWeight: 600 }}
          startOffset="50%"
          textAnchor="middle"
        >
          {label}
        </textPath>
      </text>

      {/* Tooltip */}
      {isHovered && data?.commonColumns && (
        <foreignObject
          width={250}
          height={Math.min(data.commonColumns.length * 20 + 60, 200)}
          x={labelX + 20}
          y={labelY - 100}
          style={{ overflow: "visible", pointerEvents: "none" }}
        >
          <div
            className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-2xl text-xs"
            style={{
              maxHeight: "180px",
              overflowY: "auto",
              pointerEvents: "none",
            }}
          >
            <div className="font-bold text-purple-300 mb-2">
              Common Columns ({data.commonColumns.length}):
            </div>
            <ul className="space-y-1">
              {data.commonColumns.map((col) => (
                <li key={col} className="text-gray-200">
                  • {col}
                </li>
              ))}
            </ul>
          </div>
        </foreignObject>
      )}
    </g>
  );
};

CommonColumnEdge.propTypes = {
  id: PropTypes.string,
  sourceX: PropTypes.number,
  sourceY: PropTypes.number,
  targetX: PropTypes.number,
  targetY: PropTypes.number,
  label: PropTypes.string,
  markerEnd: PropTypes.object,
  style: PropTypes.object,
  data: PropTypes.shape({
    commonColumns: PropTypes.arrayOf(PropTypes.string),
  }),
};

const nodeTypes = {
  object: ObjectNode,
  target: TargetNode,
  source: SourceNode,
};

const edgeTypes = {
  commonColumn: CommonColumnEdge,
};

// Helper function to get button className based on filter state
const getFilterButtonClassName = (isActive) => {
  return isActive
    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
    : 'bg-gray-100 text-gray-400 hover:bg-gray-200';
};

// FilterPanel component moved outside to avoid nesting
const FilterPanel = ({ filters, setFilters, setIsFilterPanelOpen }) => {
  const handleResetFilters = () => {
    setFilters({ showTargets: true, showSources: true, showCommonColumns: true, showInScopeOnly: false });
  };

  const handleToggleTargets = () => {
    setFilters(prev => ({ ...prev, showTargets: !prev.showTargets }));
  };

  const handleToggleSources = () => {
    setFilters(prev => ({ ...prev, showSources: !prev.showSources }));
  };

  const handleToggleCommonColumns = () => {
    setFilters(prev => ({ ...prev, showCommonColumns: !prev.showCommonColumns }));
  };

  const handleToggleInScope = () => {
    setFilters(prev => ({
      ...prev,
      showInScopeOnly: !prev.showInScopeOnly,
    }));
  };

  const getInScopeButtonClassName = () => {
    if (!filters.showTargets) {
      return 'bg-gray-100 text-gray-300 cursor-not-allowed';
    }
    return getFilterButtonClassName(filters.showInScopeOnly);
  };

  return (
    <div className="absolute top-24 right-96 bg-white rounded-lg shadow-xl border border-gray-200 z-40 w-64 mt-16">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-blue-600" />
        <h3 className="font-semibold text-sm text-gray-900">Filter Graph</h3>
      </div>
      <button
        type="button"
        onClick={() => setIsFilterPanelOpen(false)}
        className="p-1 hover:bg-gray-100 rounded transition-colors"
        aria-label="Close filter panel"
      >
        <X className="w-4 h-4 text-gray-500" />
      </button>
    </div>
    
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <label htmlFor="filter-targets" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
          <div className="w-5 h-5 rounded bg-cyan-100 border-2 border-cyan-500 flex-shrink-0" />
          <span>Target Structures</span>
        </label>
        <button
          id="filter-targets"
          type="button"
          onClick={handleToggleTargets}
          className={`p-1.5 rounded transition-colors ${getFilterButtonClassName(filters.showTargets)}`}
          aria-label={filters.showTargets ? "Hide target structures" : "Show target structures"}
        >
          {filters.showTargets ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
       </div>
       
     {/* IN-SCOPE ONLY FILTER */}
<div className="flex items-center justify-between">
  <label htmlFor="filter-in-scope" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
    <div
      className={`w-5 h-5 rounded border-2 flex-shrink-0 ${
        filters.showInScopeOnly
          ? 'bg-blue-100 border-blue-500'
          : 'bg-gray-100 border-gray-300'
      }`}
    />
    <span className={filters.showTargets ? '' : 'text-gray-400'}>
      In-Scope Only
    </span>
  </label>

  <button
    id="filter-in-scope"
    type="button"
    onClick={handleToggleInScope}
    disabled={!filters.showTargets}
    className={`p-1.5 rounded transition-colors ${getInScopeButtonClassName()}`}
    aria-label={filters.showInScopeOnly ? "Hide in-scope only" : "Show in-scope only"}
  >
    {filters.showInScopeOnly ? (
      <Eye className="w-4 h-4" />
    ) : (
      <EyeOff className="w-4 h-4" />
    )}
  </button>
</div>

      
      <div className="flex items-center justify-between">
        <label htmlFor="filter-sources" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
          <div className="w-5 h-5 rounded bg-green-100 border-2 border-green-500 flex-shrink-0" />
          <span>Source Tables</span>
        </label>
        <button
          id="filter-sources"
          type="button"
          onClick={handleToggleSources}
          className={`p-1.5 rounded transition-colors ${getFilterButtonClassName(filters.showSources)}`}
          aria-label={filters.showSources ? "Hide source tables" : "Show source tables"}
        >
          {filters.showSources ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      </div>
      
      <div className="flex items-center justify-between">
        <label htmlFor="filter-common-columns" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
          <div className="w-5 h-5 rounded bg-purple-100 border-2 border-purple-500 flex-shrink-0" />
          <span>Common Columns</span>
        </label>
        <button
          id="filter-common-columns"
          type="button"
          onClick={handleToggleCommonColumns}
          className={`p-1.5 rounded transition-colors ${getFilterButtonClassName(filters.showCommonColumns)}`}
          aria-label={filters.showCommonColumns ? "Hide common columns" : "Show common columns"}
        >
          {filters.showCommonColumns ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      </div>
      
      <div className="pt-3 border-t border-gray-200">
        <button
          type="button"
          onClick={handleResetFilters}
          className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
        >
          Reset All Filters
        </button>
      </div>
    </div>
  </div>
  );
};

FilterPanel.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  setIsFilterPanelOpen: PropTypes.func.isRequired,
};

const DataLineageViewer = () => {
  const [mapperFiles, setMapperFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
  showTargets: true,
  showSources: true,
    showCommonColumns: true,
    showInScopeOnly: false, // Add this new filter

});
const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [selectedCorporation, setSelectedCorporation] = useState({
    label: "Demo Corporation",
    value: "1",
  });

  // Load selected corporation from localStorage
  useEffect(() => {
    const storedCorporation = localStorage.getItem("selectedCorporation");
    if (storedCorporation) {
      const parsedCorporation = JSON.parse(storedCorporation);
      setSelectedCorporation(parsedCorporation);
    }
  }, []);

  // Listen for corporation changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "selectedCorporation") {
        const newCorporation = e.newValue ? JSON.parse(e.newValue) : null;
        setSelectedCorporation(newCorporation);
        if (newCorporation) {
          fetchMapperFiles(newCorporation.value);
        } else {
          setMapperFiles([]);
        }
      }
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail.key === "selectedCorporation") {
        const newCorporation = e.detail.newValue
          ? JSON.parse(e.detail.newValue)
          : null;
        setSelectedCorporation(newCorporation);
        if (newCorporation) {
          fetchMapperFiles(newCorporation.value);
        } else {
          setMapperFiles([]);
        }
      }
    };

    // eslint-disable-next-line no-undef
    globalThis.addEventListener("storage", handleStorageChange);
    // eslint-disable-next-line no-undef
    globalThis.addEventListener("customStorageChange", handleCustomStorageChange);

    return () => {
      // eslint-disable-next-line no-undef
      globalThis.removeEventListener("storage", handleStorageChange);
      // eslint-disable-next-line no-undef
      globalThis.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange
      );
    };
  }, []);

  const filteredNodes = nodes.filter((node) => {
    if (node.type === 'object') return true;
    if (node.type === 'target' && !filters.showTargets) return false;
    if (node.type === 'source' && !filters.showSources) return false;
    
    // Add in-scope filtering for target nodes
    if (node.type === 'target' && filters.showInScopeOnly && !node.data.isInScope) {
      return false;
    }
    return true;
  });

const filteredEdges = edges.filter((edge) => {
  // Hide edges connected to hidden nodes
  const sourceNode = nodes.find(n => n.id === edge.source);
  const targetNode = nodes.find(n => n.id === edge.target);
  
  if (!sourceNode || !targetNode) return false;
  
  if (sourceNode.type === 'target' && !filters.showTargets) return false;
  if (sourceNode.type === 'source' && !filters.showSources) return false;
  if (targetNode.type === 'target' && !filters.showTargets) return false;
  if (targetNode.type === 'source' && !filters.showSources) return false;
  
  // Hide common column edges if filter is off
  if (edge.type === 'commonColumn' && !filters.showCommonColumns) return false;
  
  return true;
});

  // Fetch mapper files when corporation changes
  useEffect(() => {
    if (selectedCorporation?.value) {
      fetchMapperFiles(selectedCorporation.value);
    }
  }, [selectedCorporation]);

  const fetchCommonColumns = async (corporationId, mappingFileId) => {
    try {
      const requestBody = {
        corporation_id: corporationId.toString(),
        mapping_file_id: mappingFileId.toString(),
      };

      console.log("Fetching common columns with:", requestBody);

      const response = await fetch(
        "https://apierd.bluegecko.ai:8004/api/common-columns",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log("Common columns response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Failed to fetch common columns:",
          response.status,
          errorText
        );
        return [];
      }

      const data = await response.json();
      console.log("Common columns data received:", data);

      if (data?.common_columns_pairs) {
        return data.common_columns_pairs;
      }
      return [];
    } catch (err) {
      console.error("Error fetching common columns:", err);
      return [];
    }
  };

  const fetchMapperFiles = async (corporationId) => {
    if (!corporationId) {
      setMapperFiles([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axiosClient.get(
        `/api/Mapper/GetMapperFiles/${corporationId}`
      );

      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : [];

        // Deduplicate files based on userMappingFileId
        const uniqueFilesMap = new Map();
        data.forEach((file) => {
          if (!uniqueFilesMap.has(file.userMappingFileId)) {
            uniqueFilesMap.set(file.userMappingFileId, file);
          }
        });

        const uniqueFiles = Array.from(uniqueFilesMap.values());

        // Sort by uploadedObjDescription for better UX
        uniqueFiles.sort((a, b) =>
          (a.uploadedObjDescription || "").localeCompare(
            b.uploadedObjDescription || ""
          )
        );

        setMapperFiles(uniqueFiles);
        console.log(
          `Loaded ${uniqueFiles.length} unique mapper files from ${data.length} total records`
        );
      } else {
        setMapperFiles([]);
        toast.error("Failed to load mapper files");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch mapper files");
      console.error("Error fetching mapper files:", err);
      toast.error(
        "Error loading mapper files: " + (err.message || "Unknown error")
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const fetchMapperFileDetails = async (userMappingFileId, fileInfo) => {
    setLoading(true);
    setError(null);
    try {
      const [detailsResponse, commonColumnsData] = await Promise.all([
        axiosClient.get(
          `/api/Mapper/GetMapperFileDetails?userMappingFileId=${userMappingFileId}`
        ),
        fetchCommonColumns(
          selectedCorporation?.value || "1",
          userMappingFileId
        ),
      ]);

      if (detailsResponse?.data) {
        const data = Array.isArray(detailsResponse.data)
          ? detailsResponse.data
          : [];
        processGraphData(data, fileInfo, commonColumnsData);
      } else {
        toast.error("Failed to fetch mapper file details");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch mapper file details");
      console.error("Error fetching mapper file details:", err);
      toast.error(
        "Error loading lineage data: " + (err.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper function to build target structure map
  const buildTargetStructureMap = (details) => {
    const targetStructureMap = new Map();
    details.forEach((item) => {
      const targetStruct = item["Target Structure (MC)"];
      const sourceTable = item["Source - Table Name"];

      if (targetStruct && targetStruct.trim() !== "" && targetStruct !== "NA") {
        if (!targetStructureMap.has(targetStruct)) {
          targetStructureMap.set(targetStruct, new Set());
        }

        if (sourceTable && sourceTable.trim() !== "" && sourceTable !== "NA") {
          targetStructureMap.get(targetStruct).add(sourceTable);
        }
      }
    });
    return targetStructureMap;
  };

  // Helper function to check if target structure is in-scope
  const checkInScope = (details, structure) => {
    return details.some(
      (item) =>
        item["Target Structure (MC)"] === structure &&
        item["In-scope"] === "Y"
    );
  };

  // Helper function to create edge from object to target
  const createObjectToTargetEdge = (centralNodeId, targetNodeId) => {
    return {
      id: `edge-obj-to-target-${targetNodeId}`,
      source: centralNodeId,
      target: targetNodeId,
      type: "straight",
      animated: false,
      style: {
        stroke: "#06b6d4",
        strokeWidth: 2.5,
      },
      markerEnd: {
        type: "arrowclosed",
        color: "#06b6d4",
        width: 20,
        height: 20,
      },
      label: "MAPS_TO",
      labelStyle: {
        fill: "#1f2937",
        fontWeight: 700,
        fontSize: 11,
        fontFamily: "system-ui, -apple-system, sans-serif",
      },
      labelBgStyle: {
        fill: "white",
        fillOpacity: 0.95,
      },
      labelBgPadding: [6, 6],
      labelBgBorderRadius: 4,
    };
  };

  // Helper function to create edge from source to target
  const createSourceToTargetEdge = (sourceNodeId, targetNodeId) => {
    return {
      id: `edge-source-to-target-${sourceNodeId}`,
      source: sourceNodeId,
      target: targetNodeId,
      type: "straight",
      animated: false,
      style: {
        stroke: "#10b981",
        strokeWidth: 2.5,
      },
      markerEnd: {
        type: "arrowclosed",
        color: "#10b981",
        width: 20,
        height: 20,
      },
      label: "SOURCES_FROM",
      labelStyle: {
        fill: "#1f2937",
        fontWeight: 700,
        fontSize: 11,
        fontFamily: "system-ui, -apple-system, sans-serif",
      },
      labelBgStyle: {
        fill: "white",
        fillOpacity: 0.95,
      },
      labelBgPadding: [6, 6],
      labelBgBorderRadius: 4,
    };
  };

  // Helper function to process common columns edges
  const processCommonColumnsEdges = (commonColumnsData, newNodes, newEdges) => {
    if (!commonColumnsData || commonColumnsData.length === 0) {
      return;
    }

    commonColumnsData.forEach((pair) => {
      const tableA = pair.table_a;
      const tableB = pair.table_b;

      const nodeA = newNodes.find(
        (node) => node.type === "source" && node.data.label === tableA
      );
      const nodeB = newNodes.find(
        (node) => node.type === "source" && node.data.label === tableB
      );

      if (nodeA && nodeB && pair.common_columns.length > 0) {
        const edgeId = `edge-common-${tableA}-${tableB}`;

        if (!newEdges.some((e) => e.id === edgeId)) {
          newEdges.push({
            id: edgeId,
            source: nodeA.id,
            target: nodeB.id,
            type: "commonColumn",
            animated: true,
            style: {
              stroke: "#8b5cf6",
              strokeWidth: 2,
              strokeDasharray: "5,5",
            },
            markerEnd: {
              type: "arrowclosed",
              color: "#8b5cf6",
              width: 15,
              height: 15,
            },
            label: `${pair.common_columns.length} common`,
            labelStyle: {
              fill: "#6b21a8",
              fontWeight: 600,
              fontSize: 10,
              fontFamily: "system-ui, -apple-system, sans-serif",
            },
            labelBgStyle: {
              fill: "#f3e8ff",
              fillOpacity: 0.95,
            },
            labelBgPadding: [4, 6],
            labelBgBorderRadius: 3,
            data: {
              commonColumns: pair.common_columns,
            },
          });
        }
      }
    });
  };

  const processGraphData = useCallback(
    (details, fileInfo, commonColumnsData = []) => {
      console.log("Processing graph data:", { details, fileInfo });

      if (!details || details.length === 0) {
        toast.warning("No mapping details found for this object");
        setNodes([]);
        setEdges([]);
        return;
      }

      const newNodes = [];
      const newEdges = [];
      const nodeIds = new Set();

      // Layout configuration
      const centerX = 600;
      const centerY = 400;
      const targetRadius = 400;
      const sourceRadius = 300;

      // Central node - Object
      const centralNodeId = `obj_${fileInfo.uploadedObjName}`;
      newNodes.push({
        id: centralNodeId,
        type: "object",
        data: {
          label: fileInfo.uploadedObjDescription || fileInfo.uploadedObjName,
        },
        position: { x: centerX, y: centerY },
      });
      nodeIds.add(centralNodeId);

      // Group data by Target Structure
      const targetStructureMap = buildTargetStructureMap(details);

      console.log(
        "Target Structure Mapping:",
        Array.from(targetStructureMap.entries()).map(([k, v]) => [
          k,
          Array.from(v),
        ])
      );

      // Create target structure nodes around the central object
      const targetStructures = Array.from(targetStructureMap.keys());
      targetStructures.forEach((structure, idx) => {
        const angle =
          (idx / Math.max(targetStructures.length, 1)) * Math.PI * 2;
        const nodeId = `target_${structure}`;

        const targetX = centerX + Math.cos(angle) * targetRadius;
        const targetY = centerY + Math.sin(angle) * targetRadius;

        const isInScope = checkInScope(details, structure);

        // Add target structure node
        newNodes.push({
          id: nodeId,
          type: "target",
          data: {
            label: structure,
            isInScope: isInScope,
          },
          position: {
            x: targetX - 65,
            y: targetY - 65,
          },
        });
        nodeIds.add(nodeId);

        // Edge from central object to target structure
        newEdges.push(createObjectToTargetEdge(centralNodeId, nodeId));

        // Add source table nodes for this target structure
        const sourceTables = Array.from(targetStructureMap.get(structure));
        sourceTables.forEach((sourceTable, sourceIdx) => {
          const sourceNodeId = `source_${structure}_${sourceTable}`;

          const sourceAngle =
            angle +
            (sourceIdx / Math.max(sourceTables.length, 1)) * Math.PI -
            Math.PI / 2;
          const sourceX = targetX + Math.cos(sourceAngle) * sourceRadius;
          const sourceY = targetY + Math.sin(sourceAngle) * sourceRadius;

          if (!nodeIds.has(sourceNodeId)) {
            newNodes.push({
              id: sourceNodeId,
              type: "source",
              data: { label: sourceTable },
              position: {
                x: sourceX - 65,
                y: sourceY - 65,
              },
            });
            nodeIds.add(sourceNodeId);

            // Edge from source table to target structure
            newEdges.push(createSourceToTargetEdge(sourceNodeId, nodeId));
          }
        });
      });

      // Process common columns edges
      processCommonColumnsEdges(commonColumnsData, newNodes, newEdges);

      console.log("Final graph data:", {
        nodes: newNodes.length,
        edges: newEdges.length,
        targetStructures: targetStructures.length,
      });

      if (newNodes.length === 1) {
        toast.info("No target structures found for this object");
      }

      setNodes(newNodes);
      setEdges(newEdges);
    },
    [setNodes, setEdges]
  );
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    fetchMapperFileDetails(file.userMappingFileId, file); // <-- Pass file as second parameter
  };

  const filteredFiles = mapperFiles.filter(
    (file) =>
      file.uploadedObjDescription
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      file.uploadedFileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full h-screen flex bg-gray-50 relative overflow-hidden">
      {" "}
      {/* Header */}
      <div className="absolute top-0 left-0 right-72 bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between h-20 z-30">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Database className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Data Lineage Viewer
            </h2>
            <p className="text-sm text-gray-500">
              Visualize data flow & relationships
            </p>
          </div>
        </div>
      </div>

{/* Filter Toggle Button */}
{selectedFile && nodes.length > 0 && (
  <button
    type="button"
    onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
    className="absolute top-24 right-96 bg-white p-3 rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 transition-all z-30 flex items-center gap-2"
    title="Filter Graph Elements"
    aria-label="Toggle filter panel"
  >
    <Filter className="w-5 h-5 text-blue-600" />
    <span className="text-sm font-medium text-gray-700">Filters</span>
    {(!filters.showTargets || !filters.showSources || !filters.showCommonColumns || filters.showInScopeOnly) && (
      <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
    )}
  </button>
)}

{/* Filter Panel */}
      {isFilterPanelOpen && <FilterPanel filters={filters} setFilters={setFilters} setIsFilterPanelOpen={setIsFilterPanelOpen} />}

  {selectedFile && nodes.length > 0 && (
  <div className="absolute top-24 left-6 bg-white px-4 py-2 rounded-lg shadow-md border border-gray-200 z-30">
    <div className="flex items-center gap-4 text-xs">
      <span className="text-gray-600">
        Showing: <strong className="text-gray-900">{filteredNodes.length}</strong> / {nodes.length} nodes
      </span>
      <span className="text-gray-300">|</span>
      <span className="text-gray-600">
        <strong className="text-gray-900">{filteredEdges.length}</strong> / {edges.length} edges
      </span>
    </div>
  </div>
)}
      
      <div className="w-full h-full flex flex-row-reverse overflow-hidden">
        {/* Sidebar */}
        <div className="w-72 bg-white border-l border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          {/* Search Bar */}
          <div className="px-2 py-3  border-gray-200 flex-shrink-0 bg-white z-30">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search objects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white placeholder-gray-500 shadow-sm"
              />
            </div>
          </div>

          {/* Files List */}
          <div className="flex-1 overflow-y-auto scrollbar-thin pb-4">
            {(() => {
              if (loading && mapperFiles.length === 0) {
                return (
                  <div className="flex items-center justify-center h-24">
                    <Loader className="w-5 h-5 animate-spin text-blue-600" />
                  </div>
                );
              }
              if (error) {
                return (
                  <div className="p-4 text-center text-red-600">
                    <p className="font-semibold text-sm">Error loading data</p>
                    <p className="text-xs">{error}</p>
                    <button
                      type="button"
                      onClick={() => fetchMapperFiles(selectedCorporation?.value)}
                      className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      Retry
                    </button>
                  </div>
                );
              }
              if (mapperFiles.length === 0) {
                return (
                  <div className="p-4 text-center text-gray-500">
                    <FileText className="mx-auto h-10 w-10 text-gray-300 mb-2" />
                    <p className="font-medium text-sm">No objects found</p>
                    <p className="text-xs">
                      {selectedCorporation?.label
                        ? `No mapper files for ${selectedCorporation.label}`
                        : "Please select a corporation"}
                    </p>
                  </div>
                );
              }
              return (
                <div className="p-2 space-y-2">
                  {filteredFiles.map((file) => (
                    <button
                      key={file.userMappingFileId}
                      type="button"
                      onClick={() => handleFileSelect(file)}
                      className={`w-full p-2.5 rounded-md cursor-pointer transition-all border text-left ${
                        selectedFile?.userMappingFileId === file.userMappingFileId
                          ? "bg-blue-50 border-blue-500"
                          : "bg-gray-50 hover:bg-gray-100 border-gray-200"
                      }`}
                    >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex space-x-2 flex-1 min-w-0">
                        <Table
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            selectedFile?.userMappingFileId ===
                            file.userMappingFileId
                              ? "text-blue-600"
                              : "text-gray-500"
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p
                            className={`font-medium text-sm break-words ${
                              selectedFile?.userMappingFileId ===
                              file.userMappingFileId
                                ? "text-blue-700"
                                : "text-gray-900"
                            }`}
                            title={file.uploadedObjDescription}
                          >
                            {file.uploadedObjDescription}
                          </p>

                          <span className="inline-block text-[10px] bg-gray-200 px-1.5 py-0.5 rounded mt-1">
                            ID: {file.userMappingFileId}
                          </span>
                        </div>
                      </div>

                      <ChevronRight
                        className={`w-4 h-4 flex-shrink-0 ${
                          selectedFile?.userMappingFileId ===
                          file.userMappingFileId
                            ? "text-blue-600"
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                  </button>
                ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Main Graph Area */}
        <div className="flex-1 flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden min-w-0 pt-20">
          {(() => {
            if (loading && selectedFile) {
              return (
                <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
                  <div className="text-center">
                    <Loader className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium text-sm">
                      Loading lineage data...
                    </p>
                  </div>
                </div>
              );
            }
            if (selectedFile) {
              if (nodes.length === 0) {
                return (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-xl text-gray-500 font-medium">
                        No lineage data available
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        This object has no mapping details
                      </p>
                    </div>
                  </div>
                );
              }
              return (
            <ReactFlow
              nodes={filteredNodes}
  edges={filteredEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              fitViewOptions={{ padding: 0.3, minZoom: 0.4, maxZoom: 1.5 }}
              defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
              minZoom={0.2}
              maxZoom={2}
              connectionLineType="straight"
              edgesUpdatable={false}
              edgesFocusable={false}
              defaultEdgeOptions={{
                type: "straight",
                animated: false,
              }}
            >
              <Background color="#d1d5db" gap={20} size={1.5} variant="dots" />
              <Controls position="top-right" showInteractive={false} />
            </ReactFlow>
              );
            }
            return (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-xl text-gray-500 font-medium">
                    Select an object to view lineage
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Choose from the list on the right
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default DataLineageViewer;
